import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Globe, Eye, Sparkles } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { useLang } from "../i18n.jsx";
import TemplateSneakPeakModal from "./TemplateSneakPeakModal.jsx";

export default function PortfolioSites() {
  const { content, lang } = useLang();
  const { sitesHead, sites, siteLabels } = content;
  const [activeSite, setActiveSite] = useState(null);

  const isId = lang !== "en";
  const sneakPeakLabel = (siteLabels && siteLabels.sneakPeak) || (isId ? "Sneak Peak" : "Sneak Peak");
  const demoLabel = (siteLabels && siteLabels.demo) || (isId ? "Demo" : "Demo");

  return (
    <section id="portofolio" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -right-24 top-1/3 w-[440px] h-[440px] rounded-full bg-acid/[0.04] blur-3xl" />
      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={motionOk ? { opacity: 0, y: 24 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-acid">{sitesHead.eyebrow}</p>
          <h2 className="font-display font-extrabold tracking-tight text-[clamp(1.8rem,4.2vw,3.2rem)] leading-[1.08] text-paper">
            {sitesHead.titleA}
            <span className="block">{sitesHead.titleB}</span>
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {sites.map((s, i) => {
            const loneLast = i === sites.length - 1 && sites.length % 4 === 1;
            return (
              <motion.div
                key={s.name}
                initial={motionOk ? { opacity: 0, y: 30 } : false}
                whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className={`group flex flex-col rounded-3xl border border-paper/10 bg-ink2/50 p-3 pb-5 transition-all hover:border-paper/25 hover:shadow-xl hover:shadow-acid/[0.02] ${
                  loneLast ? "lg:col-span-4 lg:flex-row lg:items-stretch lg:p-4" : ""
                }`}
              >
                {/* Browser Frame Preview Area */}
                <div
                  onClick={() => setActiveSite(s)}
                  className={`relative cursor-pointer overflow-hidden rounded-2xl border border-paper/10 bg-ink ${
                    loneLast ? "lg:w-1/2" : ""
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-paper/10 px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-paper/20" />
                      <span className="h-2 w-2 rounded-full bg-paper/20" />
                      <span className="h-2 w-2 rounded-full bg-paper/20" />
                    </div>
                    <span className="ml-2 flex items-center gap-1.5 truncate text-[10px] text-paper/45">
                      <Globe className="w-3 h-3" />
                      {s.domain}
                    </span>
                  </div>

                  <div
                    className="relative aspect-[4/3] overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${s.hue}26, ${s.hue}0d 60%, transparent)` }}
                  >
                    <img
                      src={s.img}
                      alt={s.name}
                      className="absolute inset-0 h-full w-full object-cover object-top opacity-95 transition-transform duration-500 group-hover:scale-105"
                    />

                    {/* Sneak Peak Hover Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 backdrop-blur-xs transition-opacity duration-300 group-hover:opacity-100">
                      <span className="flex items-center gap-1.5 rounded-full bg-acid px-3.5 py-1.5 text-xs font-bold text-ink shadow-lg shadow-acid/30 transition transform scale-90 group-hover:scale-100">
                        <Eye className="w-3.5 h-3.5" />
                        <span>{sneakPeakLabel}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Details & Action Buttons */}
                <div className={`flex flex-1 flex-col px-2 pt-4 ${loneLast ? "lg:w-1/2 lg:justify-center lg:px-6 lg:pt-0" : ""}`}>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-acid font-medium">{s.category}</span>
                  <p className="mt-1.5 text-sm text-paper/65 leading-relaxed">{s.blurb}</p>

                  <div className="mt-4 flex items-center gap-2">
                    {/* Sneak Peak Modal Trigger Button */}
                    <button
                      type="button"
                      onClick={() => setActiveSite(s)}
                      data-cursor
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-acid py-2.5 px-3 text-xs font-bold text-ink transition hover:bg-paper hover:scale-[1.02] active:scale-95 shadow-md shadow-acid/10"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>{sneakPeakLabel}</span>
                    </button>

                    {/* Direct External Link */}
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={demoLabel}
                      data-cursor
                      className="flex items-center justify-center rounded-full border border-paper/20 p-2.5 text-paper transition-colors hover:border-acid hover:text-acid"
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Embedded Live Preview Modal */}
      <TemplateSneakPeakModal
        site={activeSite}
        isOpen={!!activeSite}
        onClose={() => setActiveSite(null)}
      />
    </section>
  );
}
